import { createSignal, Show } from 'solid-js'
import './Modal.css'
import { toastStore, userStore } from '/web/store'
import LoginFormSocialButtons from './LoginFromSocialButtons'
import { usersApi } from '/web/store/data/user'
import { SetStoreFunction } from 'solid-js/store'
import { CFLogo } from '/web/shared/aifans/Logo'

interface Props {
  onClickLogin?: any
  onClickRegister?: any
  setStore: SetStoreFunction<{
    username: string
    password: string
  }>
}

const ENABLE_SOCIAL_LOGIN = true

const LoginFormModal = (props: Props) => {
  const user = userStore()

  const [showPassword, setShowPassword] = createSignal(false)
  const [showForgotPassword, setShowForgotPassword] = createSignal(false)

  const handleForgotPassword = async (e: SubmitEvent) => {
    e.preventDefault()
    const form = e.target as HTMLFormElement
    const email = form.email.value
    try {
      const res = await usersApi.forgotPassword(email)

      if (res.error) {
        toastStore.error(`${res.error}`)
        return
      }

      toastStore.success(
        'If an account exists with that email, you will receive reset instructions shortly'
      )

      form.reset()
      setShowForgotPassword(false)
    } catch (error) {
      toastStore.error(`Error sending forgot password email: ${error}`)
    }
  }

  const closeModal = () => {
    userStore.loginModal(false)
    setShowForgotPassword(false)
  }

  return (
    <Show when={user.showLogin && !user.loginModalRedirect?.startsWith('/hentai')}>
      <div
        class="fixed inset-0 z-[200] flex items-center justify-center overflow-y-auto"
        style="background: rgba(0,0,0,0.90)"
        onClick={closeModal}
      >
        <div
          class="relative w-full max-w-[420px] rounded-[18px] border border-[rgba(0,229,255,0.18)] px-[30px] pb-[26px] pt-8 max-h-[calc(100vh-40px)] overflow-y-auto"
          style="background: linear-gradient(to bottom, #14101f, #0c0916); box-shadow: 0 30px 80px rgba(138,43,226,0.28)"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={closeModal}
            class="absolute top-[14px] right-[14px] w-8 h-8 rounded-full border border-[var(--cf-hairline)] grid place-items-center text-[22px] leading-none transition-all duration-200 hover:rotate-90"
            style="background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.55)"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,60,172,0.18)'
              e.currentTarget.style.color = 'white'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
              e.currentTarget.style.color = 'rgba(255,255,255,0.55)'
            }}
          >
            &#10005;
          </button>

          {/* Logo */}
          <div class="flex justify-center mb-[22px]">
            <CFLogo size="md" showSub />
          </div>

          <Show
            when={!showForgotPassword()}
            fallback={
              <>
                {/* Forgot Password Title */}
                <div class="font-bebas text-[32px] tracking-[1.5px] text-center text-white leading-[1.1]">
                  Reset Password
                </div>
                <div class="font-rajdhani text-[13px] font-semibold tracking-[0.6px] text-center text-[var(--cf-muted)] mt-1 mb-[22px]">
                  Enter your email and we'll send reset instructions
                </div>

                <form onSubmit={handleForgotPassword}>
                  <div class="flex flex-col gap-[14px]">
                    <div>
                      <label class="block font-rajdhani text-[10px] font-bold tracking-[1.4px] uppercase text-[rgba(255,255,255,0.55)] mb-[6px]">
                        Email Address
                      </label>
                      <input
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                        title="Please enter a valid email address"
                        required
                        class="w-full h-[44px] px-[14px] rounded-[10px] border border-[var(--cf-hairline)] text-white font-nunito text-[14px] outline-none transition-all duration-200 focus:border-[rgba(0,229,255,0.5)] focus:shadow-[0_0_0_3px_rgba(0,229,255,0.12)]"
                        style="background: rgba(255,255,255,0.04)"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    class="mt-[6px] w-full h-[48px] rounded-[12px] border-none text-white font-rajdhani font-extrabold text-[14px] tracking-[1px] uppercase cursor-pointer transition-all duration-200 hover:translate-y-[-2px]"
                    style="background: var(--cf-grad-hot); box-shadow: 0 8px 24px rgba(138,43,226,0.35)"
                  >
                    Reset Password
                  </button>
                </form>

                <div class="mt-[18px] text-center font-rajdhani text-[13px] font-semibold text-[rgba(255,255,255,0.5)]">
                  Remember your password?
                  <a
                    href="#"
                    class="text-[var(--cf-cyan)] font-bold ml-1 cursor-pointer hover:underline"
                    onClick={(e) => {
                      e.preventDefault()
                      setShowForgotPassword(false)
                    }}
                  >
                    Log In
                  </a>
                </div>
              </>
            }
          >
            {/* Login Title */}
            <div class="font-bebas text-[32px] tracking-[1.5px] text-center text-white leading-[1.1]">
              Welcome Back
            </div>
            <div class="font-rajdhani text-[13px] font-semibold tracking-[0.6px] text-center text-[var(--cf-muted)] mt-1 mb-[22px]">
              Log in to your account
            </div>

            {/* Google Sign In */}
            <LoginFormSocialButtons show={ENABLE_SOCIAL_LOGIN} user={user} />

            <form
              onSubmit={(e) => {
                e.preventDefault()
                props.onClickLogin()
              }}
            >
              <div class="flex flex-col gap-[14px]">
                <div>
                  <label class="block font-rajdhani text-[10px] font-bold tracking-[1.4px] uppercase text-[rgba(255,255,255,0.55)] mb-[6px]">
                    Email or Username
                  </label>
                  <input
                    name="username"
                    placeholder="Enter your email or username"
                    autocomplete="username"
                    class="w-full h-[44px] px-[14px] rounded-[10px] border border-[var(--cf-hairline)] text-white font-nunito text-[14px] outline-none transition-all duration-200 placeholder:text-[rgba(255,255,255,0.28)] focus:border-[rgba(0,229,255,0.5)] focus:bg-[rgba(0,229,255,0.04)] focus:shadow-[0_0_0_3px_rgba(0,229,255,0.12)]"
                    style="background: rgba(255,255,255,0.04)"
                    onInput={(ev) => props.setStore('username', ev.currentTarget.value)}
                  />
                </div>
                <div>
                  <label class="block font-rajdhani text-[10px] font-bold tracking-[1.4px] uppercase text-[rgba(255,255,255,0.55)] mb-[6px]">
                    Password
                  </label>
                  <div class="relative">
                    <input
                      name="password"
                      type={showPassword() ? 'text' : 'password'}
                      placeholder="Enter your password"
                      autocomplete="current-password"
                      class="w-full h-[44px] px-[14px] rounded-[10px] border border-[var(--cf-hairline)] text-white font-nunito text-[14px] outline-none transition-all duration-200 placeholder:text-[rgba(255,255,255,0.28)] focus:border-[rgba(0,229,255,0.5)] focus:bg-[rgba(0,229,255,0.04)] focus:shadow-[0_0_0_3px_rgba(0,229,255,0.12)]"
                      style="background: rgba(255,255,255,0.04)"
                      onInput={(ev) => props.setStore('password', ev.currentTarget.value)}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter') {
                          ev.preventDefault()
                          props.onClickLogin()
                        }
                      }}
                    />
                    <button
                      type="button"
                      class="absolute right-3.5 top-1/2 flex -translate-y-1/2 items-center border-none bg-transparent p-1 transition-colors cursor-pointer"
                      style="color: rgba(255,255,255,0.4)"
                      onClick={() => setShowPassword(!showPassword())}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div class="mt-[-6px] text-right">
                <a
                  href="#"
                  class="font-rajdhani text-[12px] font-semibold text-[var(--cf-cyan)] hover:underline"
                  onClick={(e) => {
                    e.preventDefault()
                    setShowForgotPassword(true)
                  }}
                >
                  Forgot Password?
                </a>
              </div>

              <button
                type="submit"
                class="mt-[6px] w-full h-[48px] rounded-[12px] border-none text-white font-rajdhani font-extrabold text-[14px] tracking-[1px] uppercase cursor-pointer transition-all duration-200 hover:translate-y-[-2px]"
                style="background: var(--cf-grad-hot); box-shadow: 0 8px 24px rgba(138,43,226,0.35)"
              >
                Log In
              </button>
            </form>

            <div class="mt-[18px] text-center font-rajdhani text-[13px] font-semibold text-[rgba(255,255,255,0.5)]">
              Don't have an account?
              <a
                href="#"
                class="text-[var(--cf-cyan)] font-bold ml-1 cursor-pointer hover:underline"
                onClick={(e) => {
                  e.preventDefault()
                  props.onClickRegister(e)
                  setShowForgotPassword(false)
                }}
              >
                Register
              </a>
            </div>
          </Show>
        </div>
      </div>
    </Show>
  )
}

export default LoginFormModal
